import { z } from "zod";
import { FactualAssessment } from "./factual-assessment.js";
import { OPPORTUNITY_REF_PATTERN, WON_JOB_REF_PATTERN } from "./job-ref.js";
export const WorkStage = z.enum(["opportunity", "job"]);
export const WorkSurface = z.enum(["mobile", "aiw", "installer_pwa"]);
export const WorkDevice = z.object({
    id: z.string().min(1).max(160),
    name: z.string().trim().min(1).max(160),
    surface: WorkSurface,
});
/**
 * The shared document deliberately keeps the existing assessment, installer and
 * asset payloads whole. Their own versioned schemas remain authoritative while
 * Mobile, AIW and the PWA move onto one record.
 */
export const WorkDocument = z.object({
    schema: z.literal("artemis.work-record/1"),
    assessment_contract: z.literal("artemis.factual-assessment/1").default("artemis.factual-assessment/1"),
    assessment: FactualAssessment.nullable().default(null),
    installer: z.record(z.string(), z.unknown()).nullable().default(null),
    site_details: z.record(z.string(), z.unknown()).default({}),
    assets: z.record(z.string(), z.unknown()).default({}),
    quantities: z.record(z.string(), z.unknown()).default({}),
}).passthrough();
export const WorkRevisionSource = z.object({
    revision_id: z.string().uuid(),
    owner_user_id: z.string().uuid(),
    owner_name: z.string().trim().min(1).max(160),
    save_number: z.number().int().positive(),
    saved_at: z.iso.datetime({ offset: true }),
    content_hash: z.string().regex(/^[a-f0-9]{64}$/),
});
export const WorkRevision = z.object({
    id: z.string().uuid(),
    work_id: z.string().uuid(),
    owner_user_id: z.string().uuid(),
    owner_name: z.string().trim().min(1).max(160),
    save_number: z.number().int().positive(),
    saved_at: z.iso.datetime({ offset: true }),
    device: WorkDevice,
    idempotency_key: z.string().min(8).max(200),
    content_hash: z.string().regex(/^[a-f0-9]{64}$/),
    source: WorkRevisionSource.nullable().default(null),
    document: WorkDocument,
});
export const WorkRecord = z.object({
    id: z.string().uuid(),
    stage: WorkStage,
    opportunity_ref: z.string().regex(OPPORTUNITY_REF_PATTERN).nullable(),
    job_ref: z.string().regex(WON_JOB_REF_PATTERN).nullable(),
    title: z.string().trim().min(1).max(240),
    updated_at: z.iso.datetime({ offset: true }),
}).superRefine((record, ctx) => {
    if (!record.opportunity_ref && !record.job_ref)
        ctx.addIssue({ code: "custom", path: ["opportunity_ref"], message: "A work record needs an opportunity or job reference" });
    if (record.stage === "opportunity" && !record.opportunity_ref)
        ctx.addIssue({ code: "custom", path: ["opportunity_ref"], message: "Opportunity stage needs its opportunity reference" });
    if (record.stage === "job" && !record.job_ref)
        ctx.addIssue({ code: "custom", path: ["job_ref"], message: "Job stage needs its job reference" });
});
export const SaveWorkRevisionInput = z.object({
    work_id: z.string().uuid(),
    expected_latest_revision_id: z.string().uuid().nullable(),
    source_revision_id: z.string().uuid().nullable().default(null),
    device: WorkDevice,
    idempotency_key: z.string().min(8).max(200),
    document: WorkDocument,
    // Request capability, deliberately outside the permanent document.
    geometry_capabilities: z.array(z.string().min(1).max(100)).max(16).default([]),
});
export function sourceOf(revision) {
    return {
        revision_id: revision.id,
        owner_user_id: revision.owner_user_id,
        owner_name: revision.owner_name,
        save_number: revision.save_number,
        saved_at: revision.saved_at,
        content_hash: revision.content_hash,
    };
}
/** @deprecated Explicit online revisions are permanent; local drafts own recovery. */
export function retainLatestWorkRevisions(revisions) {
    return [...revisions].sort((a, b) => b.saved_at.localeCompare(a.saved_at) || b.save_number - a.save_number || b.id.localeCompare(a.id));
}
//# sourceMappingURL=work-record.js.map